/**
 * Standardized localStorage utility for Syncreos
 * This provides a consistent interface for storing and retrieving data
 * with proper type safety, versioning, and fallback to mock data.
 */

// Prefix for all Syncreos localStorage keys
const PREFIX = 'syncreos-';

// Version for data schema - increment when schema changes to trigger migrations
const CURRENT_VERSION = 1;

// Default cache expiration (24 hours in milliseconds)
const DEFAULT_EXPIRATION = 24 * 60 * 60 * 1000;

// Interface for stored data with metadata
interface StoredData<T> {
    version: number;
    timestamp: number;
    expiration: number;
    data: T;
}

/**
 * Save data to localStorage with metadata
 * @param key Storage key (will be prefixed)
 * @param data Data to store
 * @param expiration Optional expiration time in milliseconds
 */
export function saveToStorage<T>(key: string, data: T, expiration = DEFAULT_EXPIRATION): void {
    try {
        const storedData: StoredData<T> = {
            version: CURRENT_VERSION,
            timestamp: Date.now(),
            expiration,
            data
        };
        localStorage.setItem(PREFIX + key, JSON.stringify(storedData));
        console.log(`[localStorage] saveToStorage('${key}'): Saved data:`, data);
    } catch (error) {
        console.error(`Error saving to localStorage: ${key}`, error);
    }
}

/**
 * Load data from localStorage
 * @param key Storage key (will be prefixed)
 * @param fallbackData Optional fallback data if not found or expired
 * @returns The stored data or fallback
 */
export function loadFromStorage<T>(key: string, fallbackData?: T): T | undefined {
    try {
        const rawData = localStorage.getItem(PREFIX + key);
        console.log(`[localStorage] loadFromStorage('${key}'): rawData exists?`, !!rawData);

        if (!rawData) {
            return fallbackData;
        }

        const storedData = JSON.parse(rawData) as StoredData<T>;

        // Check if data is expired
        if (Date.now() > storedData.timestamp + storedData.expiration) {
            console.log(`[localStorage] Data expired for key: ${key}`);
            return fallbackData;
        }

        // Check if schema version is outdated (for future migrations)
        if (storedData.version < CURRENT_VERSION) {
            console.log(`[localStorage] Data version outdated for key: ${key}`);
            return migrateData(key, storedData) || fallbackData;
        }

        console.log(`[localStorage] loadFromStorage('${key}'): Loaded data:`, storedData.data);
        return storedData.data;
    } catch (error) {
        console.error(`Error loading from localStorage: ${key}`, error);
        return fallbackData;
    }
}

/**
 * Remove data from localStorage
 * @param key Storage key (will be prefixed)
 */
export function removeFromStorage(key: string): void {
    try {
        localStorage.removeItem(PREFIX + key);
    } catch (error) {
        console.error(`Error removing from localStorage: ${key}`, error);
    }
}

/**
 * Clear all Syncreos data from localStorage
 */
export function clearAllStorage(): void {
    try {
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith(PREFIX)) {
                localStorage.removeItem(key);
            }
        });
    } catch (error) {
        console.error('Error clearing localStorage', error);
    }
}

/**
 * Get all keys from localStorage that match the prefix
 * @param keyPrefix Optional additional prefix after the Syncreos prefix
 * @returns Array of keys without the prefix
 */
export function getStorageKeys(keyPrefix = ''): string[] {
    try {
        const fullPrefix = PREFIX + keyPrefix;
        const keys = Object.keys(localStorage)
            .filter(key => key.startsWith(fullPrefix))
            .map(key => key.substring(PREFIX.length));
        console.log(`[localStorage] getStorageKeys('${keyPrefix}') found keys:`, keys);
        return keys;
    } catch (error) {
        console.error(`Error getting localStorage keys with prefix: ${keyPrefix}`, error);
        return [];
    }
}

/**
 * Data migration function for handling version changes
 * @param key Storage key
 * @param storedData The outdated stored data
 * @returns Migrated data or undefined if migration failed
 */
function migrateData<T>(key: string, storedData: StoredData<T>): T | undefined {
    // This would contain migration logic for different schema versions
    // For now, just return the data as-is
    console.log(`Migration needed for ${key} from version ${storedData.version} to ${CURRENT_VERSION}`);
    return storedData.data;
}

/**
 * Helper to generate a storage key with a specific format
 * @param collection The collection name (e.g., 'projects', 'users')
 * @param id Optional ID for a specific item
 * @param property Optional property name
 * @returns Formatted storage key
 */
export function createStorageKey(collection: string, id?: string, property?: string): string {
    if (id && property) {
        return `${collection}/${id}/${property}`;
    }
    if (id) {
        return `${collection}/${id}`;
    }
    return collection;
}

/**
 * Save a collection item with automatic key generation
 * @param collection Collection name
 * @param id Item ID
 * @param data Item data
 */
export function saveCollectionItem<T>(collection: string, id: string, data: T): void {
    saveToStorage(createStorageKey(collection, id), data);
}

/**
 * Load a collection item with automatic key generation
 * @param collection Collection name
 * @param id Item ID
 * @param fallbackData Optional fallback data
 * @returns The item data or fallback
 */
export function loadCollectionItem<T>(collection: string, id: string, fallbackData?: T): T | undefined {
    return loadFromStorage(createStorageKey(collection, id), fallbackData);
}

/**
 * Get all items in a collection
 * @param collection Collection name
 * @returns Array of [id, data] tuples
 */
export function getAllCollectionItems<T>(collection: string): [string, T][] {
    try {
        const keys = getStorageKeys(`${collection}/`);
        return keys.map(key => {
            const id = key.split('/')[1];
            const data = loadFromStorage<T>(key);
            return [id, data!];
        }).filter(([, data]) => data !== undefined) as [string, T][];
    } catch (error) {
        console.error(`Error getting all items from collection: ${collection}`, error);
        return [];
    }
} 